import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from 'app/components/ui/card';
import {
  Smartphone,
  Shield,
  CheckCircle,
  AlertCircle,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  Download,
} from 'lucide-react';
import { Switch } from 'app/components/ui/switch';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { toast } from 'react-hot-toast';
import { useProfileSlice } from '../slice';
import { useToast } from 'app/components/ui/use-toast';

export function TwoFATab() {
  const {
    useGetMicrosoft2FAStatusQuery,
    useSetupMicrosoft2FAMutation,
    useVerifyMicrosoft2FASetupMutation,
    useDisableMicrosoft2FAMutation,
    useVerifyMicrosoft2FATokenMutation,
    useRegenerateBackupCodesMutation,
    useGetBackupCodesStatusQuery,
    useGetBackupCodesQuery,
  } = useProfileSlice();

  // State management
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [qrCodeData, setQrCodeData] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisablePassword, setShowDisablePassword] = useState(false);
  const [disableError, setDisableError] = useState('');
  const [backupCodesStatus, setBackupCodesStatus] = useState<any>(null);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [regeneratePassword, setRegeneratePassword] = useState('');
  const { toast } = useToast();

  // API hooks
  const [get2FAStatus, { isLoading: statusLoading }] =
    useGetMicrosoft2FAStatusQuery();
  const [setup2FA, { isLoading: setupLoading }] =
    useSetupMicrosoft2FAMutation();
  const [verify2FASetup, { isLoading: verifySetupLoading }] =
    useVerifyMicrosoft2FASetupMutation();
  const [disable2FA, { isLoading: disableLoading }] =
    useDisableMicrosoft2FAMutation();
  const [verify2FAToken, { isLoading: verifyTokenLoading }] =
    useVerifyMicrosoft2FATokenMutation();
  const [regenerateBackupCodes, { isLoading: regenerateLoading }] =
    useRegenerateBackupCodesMutation();
  const [getBackupCodesStatus, { isLoading: statusBackupCodesLoading }] =
    useGetBackupCodesStatusQuery();
  const [getBackupCodes, { isLoading: getBackupCodesLoading }] =
    useGetBackupCodesQuery();

  // Load 2FA status and backup codes status on component mount
  useEffect(() => {
    load2FAStatus();
    loadBackupCodesStatus();
  }, []);

  const load2FAStatus = async () => {
    try {
      const result = await get2FAStatus();
      if (result.data) {
        setIs2FAEnabled(
          result.data.status === 'enabled' || result.data.enabled || false,
        );
        if (result.data.backupCodes) {
          setBackupCodes(result.data.backupCodes);
        }
      }
    } catch (error) {
      console.error('Failed to load 2FA status:', error);
    }
  };

  const loadBackupCodesStatus = async () => {
    try {
      const result = await getBackupCodesStatus();
      if (result.data) {
        setBackupCodesStatus(result.data);
        // If backup codes are returned, update the backup codes state
        if (result.data.backupCodes) {
          setBackupCodes(result.data.backupCodes);
        }
      }
    } catch (error) {
      console.error('Failed to load backup codes status:', error);
    }
  };

  const handle2FAToggle = async (enabled: boolean) => {
    if (enabled) {
      await setup2FAFlow();
    } else {
      setDisableError('');
      setShowDisableDialog(true);
    }
  };

  const setup2FAFlow = async () => {
    try {
      setIsLoading(true);
      setError('');

      const result = await setup2FA({});
      if (result.data) {
        setQrCodeData(result.data.qrCode || '');
        setSecretKey(result.data.secret || result.data.manualEntryKey || '');
        setShow2FASetup(true);
        toast.success(
          result.data.message ||
            '2FA setup initiated. Please scan the QR code.',
        );
      }
    } catch (error: any) {
      setError(error.data?.message || 'Failed to setup 2FA');
      toast({
        title: 'Error',
        description: 'Failed to setup 2FA',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast({
        title: 'Error',
        description: 'Please enter a valid 6-digit code',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const result = await verify2FASetup({
        code: verificationCode,
        secret: secretKey,
      });

      if (result.data) {
        setIs2FAEnabled(true);
        setShow2FASetup(false);
        setBackupCodes(result.data.backupCodes || []);
        setVerificationCode('');
        setQrCodeData('');
        setSecretKey('');
        toast({
          title: 'Success',
          description: result.data.message || '2FA enabled successfully!',
          variant: 'success',
        });
      }
    } catch (error: any) {
      setError(error.data?.message || 'Invalid verification code');
      toast({
        title: 'Error',
        description: 'Invalid verification code',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const disable2FAFlow = async (password: string) => {
    try {
      setIsLoading(true);
      setDisableError('');

      const result = await disable2FA({ password });
      console.log('disable2FA result:', result);

      if (result.data) {
        setIs2FAEnabled(false);
        setBackupCodes([]);
        setShowDisableDialog(false);
        setDisablePassword('');
        setDisableError('');
        toast({
          title: 'Success',
          description: result.data.message || '2FA disabled successfully',
          variant: 'success',
        });
      } else if (result.error) {
        // Handle API error response
        const errorMessage =
          result.error.data?.error ||
          result.error.message ||
          'Failed to disable 2FA';
        setDisableError(errorMessage);
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.log('catch error', error);
      const errorMessage =
        error.data?.message || error.message || 'Failed to disable 2FA';
      setDisableError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter your password',
        variant: 'destructive',
      });
      return;
    }

    await disable2FAFlow(disablePassword);
  };

  const handleCancelDisable = () => {
    setShowDisableDialog(false);
    setDisablePassword('');
    setDisableError('');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Success',
      description: 'Copied to clipboard',
      variant: 'success',
    });
  };

  const downloadBackupCodes = async () => {
    try {
      const result = await getBackupCodes();
      if (result.data && result.data.backupCodes) {
        const codesText = result.data.backupCodes.join('\n');
        const blob = new Blob([codesText], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = '2fa-backup-codes.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast({
          title: 'Success',
          description: 'Backup codes downloaded successfully',
          variant: 'success',
        });
      } else {
        toast({
          title: 'Error',
          description: 'No backup codes available to download',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to download backup codes:', error);
      toast({
        title: 'Error',
        description: 'Failed to download backup codes',
        variant: 'destructive',
      });
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!regeneratePassword.trim()) {
      toast({
        title: 'Error',
        description: 'Password is required to regenerate backup codes',
        variant: 'destructive',
      });
      return;
    }

    // Test toast to ensure toast system is working
    console.log('Starting regenerate backup codes...');
    toast({
      title: 'Error',
      description: 'Test error message - if you see this, toast is working',
      variant: 'destructive',
    });

    try {
      setIsLoading(true);
      const result = await regenerateBackupCodes({
        password: regeneratePassword,
      });
      console.log('result', result);
      if (result.data) {
        setBackupCodes(result.data.backupCodes || result.data.codes || []);
        toast({
          title: 'Success',
          description: 'Backup codes regenerated successfully',
          variant: 'success',
        });
        setShowRegenerateDialog(false);
        setRegeneratePassword('');
        // Refresh backup codes status after regeneration
        await loadBackupCodesStatus();
      } else {
        console.log('result.error', result.error);
        // Handle error response
        const errorMessage =
          result.error?.data?.error || 'Failed to regenerate backup codes';
        console.log('errorMessage', errorMessage);
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.log('error', error);
      console.error('Failed to regenerate backup codes:', error);

      // Handle different error formats
      let errorMessage = 'Failed to regenerate backup codes';

      if (error?.data?.error) {
        errorMessage = error.data.error;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isAnyLoading =
    statusLoading ||
    setupLoading ||
    verifySetupLoading ||
    disableLoading ||
    verifyTokenLoading ||
    regenerateLoading ||
    statusBackupCodesLoading ||
    isLoading;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account with 2FA.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Two-Factor Authentication</h3>
              <p className="text-sm text-gray-500">
                {is2FAEnabled
                  ? 'Your account is protected with 2FA'
                  : 'Enable 2FA to secure your account'}
              </p>
            </div>
            <Switch
              checked={is2FAEnabled}
              onCheckedChange={handle2FAToggle}
              disabled={isAnyLoading}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-sm text-red-800 !mb-0">{error}</p>
              </div>
            </div>
          )}

          {is2FAEnabled && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm text-green-800 font-medium mb-0">
                    2FA is Enabled
                  </p>
                  <p className="text-sm text-green-700 mt-1 mb-0">
                    Your account is now protected with two-factor
                    authentication.
                  </p>
                </div>
              </div>
            </div>
          )}

          {show2FASetup && !is2FAEnabled && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Setup 2FA</CardTitle>
                <CardDescription>
                  Scan the QR code with your authenticator app or enter the
                  secret key manually.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {qrCodeData && (
                  <div className="flex justify-center">
                    <div className="w-48 h-48 bg-white border rounded-lg flex items-center justify-center">
                      <img
                        src={qrCodeData}
                        alt="2FA QR Code"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                )}

                {secretKey && (
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">
                      Or enter this secret key manually:
                    </p>
                    <div className="flex items-center gap-2 justify-center">
                      <code className="bg-gray-100 px-3 py-1 rounded text-sm font-mono">
                        {secretKey}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(secretKey)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="2fa_code">Verification Code</Label>
                  <Input
                    id="2fa_code"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    value={verificationCode}
                    onChange={e =>
                      setVerificationCode(e.target.value.replace(/\D/g, ''))
                    }
                    disabled={isAnyLoading}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={handleVerifySetup}
                    disabled={isAnyLoading || !verificationCode}
                  >
                    {isAnyLoading && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    Verify & Enable
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShow2FASetup(false);
                      setVerificationCode('');
                      setQrCodeData('');
                      setSecretKey('');
                      setError('');
                    }}
                    disabled={isAnyLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {showDisableDialog && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-red-600">
                  Disable 2FA
                </CardTitle>
                <CardDescription>
                  To disable two-factor authentication, please enter your
                  current password for security verification.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {disableError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <p className="text-sm text-red-800">{disableError}</p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="disable_password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="disable_password"
                      type={showDisablePassword ? 'text' : 'password'}
                      placeholder="Enter your current password"
                      value={disablePassword}
                      onChange={e => {
                        setDisablePassword(e.target.value);
                        if (disableError) setDisableError('');
                      }}
                      disabled={isAnyLoading}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() =>
                        setShowDisablePassword(!showDisablePassword)
                      }
                      disabled={isAnyLoading}
                    >
                      {showDisablePassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Warning:</strong> Disabling 2FA will reduce the
                    security of your account. Make sure you understand the risks
                    before proceeding.
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={handleDisable2FA}
                    disabled={isAnyLoading || !disablePassword.trim()}
                  >
                    {isAnyLoading && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    Disable 2FA
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancelDisable}
                    disabled={isAnyLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {is2FAEnabled && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Backup Codes</CardTitle>
                <CardDescription>
                  Save these backup codes in a safe place. You can use them to
                  access your account if you lose your authenticator device.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-center">
                  <div className="flex gap-3">
                    {backupCodesStatus?.hasBackupCodes ? (
                      <>
                        <Button
                          variant="outline"
                          onClick={downloadBackupCodes}
                          disabled={getBackupCodesLoading}
                          className="px-6 py-2 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all duration-200"
                        >
                          {getBackupCodesLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-2" />
                              Download Codes
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => setShowRegenerateDialog(true)}
                          disabled={regenerateLoading}
                          className="px-6 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
                        >
                          {regenerateLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Regenerating...
                            </>
                          ) : (
                            <>
                              <Shield className="w-4 h-4 mr-2" />
                              Regenerate Codes
                            </>
                          )}
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => setShowRegenerateDialog(true)}
                        disabled={regenerateLoading}
                        className="px-8 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 text-lg"
                      >
                        {regenerateLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Shield className="w-5 h-5 mr-2" />
                            Generate Backup Codes
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Backup Codes Status Display */}
                {backupCodesStatus && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900">
                        Backup Codes Status
                      </h4>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            backupCodesStatus.hasBackupCodes
                              ? 'bg-green-500'
                              : 'bg-gray-400'
                          }`}
                        ></div>
                        <span className="text-xs text-gray-600">
                          {backupCodesStatus.hasBackupCodes
                            ? 'Available'
                            : 'Not Available'}
                        </span>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        {backupCodesStatus.backupCodesCount || 0}
                      </div>
                      <div className="text-sm text-gray-600">
                        Backup codes remaining
                      </div>
                    </div>
                    {backupCodesStatus.warning && (
                      <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                        {backupCodesStatus.warning}
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> Each backup code can only be
                    used once. Generate new codes if you run out or if you
                    suspect they've been compromised.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm text-blue-800 font-medium">
                  2FA Benefits
                </p>
                <ul className="text-sm text-blue-700 mt-2 space-y-1">
                  <li>• Enhanced security with second authentication factor</li>
                  <li>• Protection against unauthorized access</li>
                  <li>
                    • Works with Google Authenticator, Authy, Microsoft
                    Authenticator, and other apps
                  </li>
                  <li>• Backup codes available for account recovery</li>
                  <li>
                    • Industry-standard TOTP (Time-based One-Time Password)
                    algorithm
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Regenerate Backup Codes Confirmation Dialog */}
      {showRegenerateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="text-center mb-4">
              <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Regenerate Backup Codes
              </h3>
              <p className="text-sm text-gray-600 mt-2">
                This will generate new backup codes and invalidate all existing
                ones. Make sure you have access to your authenticator app before
                proceeding.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> All current backup codes will become
                invalid. You'll need to save the new codes immediately.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="regenerate_password">Current Password</Label>
                <Input
                  id="regenerate_password"
                  type="password"
                  placeholder="Enter your current password"
                  value={regeneratePassword}
                  onChange={e => setRegeneratePassword(e.target.value)}
                  disabled={regenerateLoading}
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowRegenerateDialog(false);
                  setRegeneratePassword('');
                }}
                disabled={regenerateLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleRegenerateBackupCodes}
                disabled={regenerateLoading || !regeneratePassword.trim()}
              >
                {regenerateLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  'Regenerate Codes'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
